
import { useState } from 'react';

export default function ReportDownloadForm() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('submitting');
    const form = e.currentTarget;
    const formData = new FormData(form);

    const emailVal = formData.get('email') as string;
    if (!emailVal || !emailVal.includes('@')) {
      setStatus('idle');
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d63f8p1ov5qgbon31tsg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setTimeout(() => setStatus('idle'), 5000);
      }
    } catch {
      setStatus('error');
      setTimeout(() => setStatus('idle'), 5000);
    }
  };

  return (
    <section className="py-20 px-6 bg-white border-t border-gray-100">
      <div className="max-w-6xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left — Content */}
          <div>
            <span className="inline-block text-emerald-700 font-bold text-xs tracking-widest uppercase mb-4">
              Free Download
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mb-5 tracking-wide text-gray-900">
              Get the Full ROI Report for Your Operation
            </h2>
            <p className="text-base text-gray-600 leading-relaxed mb-6 font-light">
              We&apos;ll send you a detailed PDF covering real-world savings data, per-acre breakdowns by crop type, and a side-by-side comparison of ground vs. aerial economics — built from 50,000+ acres of field data.
            </p>

            <ul className="space-y-3 mb-8">
              {[
                'Per-acre savings by crop type (corn, soybeans, wheat, alfalfa)',
                'Compaction cost analysis with USDA-sourced benchmarks',
                'Spray timing windows and the true cost of missing them',
                'Deer browse damage estimates for edge-row acreage',
                '5-year compounding loss projections',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <div className="w-5 h-5 flex items-center justify-center mt-0.5 shrink-0">
                    <i className="ri-checkbox-circle-line text-emerald-600"></i>
                  </div>
                  <span className="text-sm text-gray-700">{item}</span>
                </li>
              ))}
            </ul>

            {/* Red callout */}
            <div className="border-l-3 border-red-500 bg-red-50/60 rounded-r-lg px-4 py-3">
              <p className="text-sm text-gray-700">
                Most operations underestimate annual losses by <strong className="text-red-600">$40–$130+ per acre</strong>. This report shows you exactly where the money goes.
              </p>
            </div>
          </div>

          {/* Right — Form Card */}
          <div className="bg-stone-50 border border-gray-200 rounded-lg p-8 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 flex items-center justify-center bg-emerald-100 rounded-lg">
                <i className="ri-file-download-line text-xl text-emerald-700"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900">Download the Report</h3>
            </div>
            <p className="text-sm text-gray-500 mb-6">
              Enter your details and we&apos;ll email you the full report — no obligation, no spam.
            </p>

            {status === 'success' ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-emerald-100 rounded-full">
                  <i className="ri-check-line text-3xl text-emerald-700"></i>
                </div>
                <h4 className="text-lg font-bold text-gray-900 mb-2">Check Your Inbox</h4>
                <p className="text-sm text-gray-600 leading-relaxed max-w-xs mx-auto">
                  Your ROI report is on its way. If you don&apos;t see it within a few minutes, check your spam folder.
                </p>
              </div>
            ) : (
              <form
                data-readdy-form
                id="roi-report-download-form"
                onSubmit={handleSubmit}
                className="space-y-4"
              >
                <input type="hidden" name="formSource" value="ROI Report Download" />

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label htmlFor="report-firstName" className="block text-sm font-bold mb-1.5 text-gray-800">
                      First Name <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="text"
                      id="report-firstName"
                      name="firstName"
                      required
                      placeholder="John"
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-700 focus:border-transparent text-sm bg-white"
                    />
                  </div>
                  <div>
                    <label htmlFor="report-lastName" className="block text-sm font-bold mb-1.5 text-gray-800">
                      Last Name <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="text"
                      id="report-lastName"
                      name="lastName"
                      required
                      placeholder="Smith"
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-700 focus:border-transparent text-sm bg-white"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="report-email" className="block text-sm font-bold mb-1.5 text-gray-800">
                    Email <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="email"
                    id="report-email"
                    name="email"
                    required
                    placeholder="john@example.com"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-700 focus:border-transparent text-sm bg-white"
                  />
                </div>

                <div>
                  <label htmlFor="report-acreage" className="block text-sm font-bold mb-1.5 text-gray-800">
                    Total Acreage <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <input
                    type="number"
                    id="report-acreage"
                    name="acreage"
                    min="0"
                    placeholder="e.g. 1,200"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-700 focus:border-transparent text-sm bg-white"
                  />
                </div>

                <div>
                  <label htmlFor="report-crop" className="block text-sm font-bold mb-1.5 text-gray-800">
                    Primary Crop <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <select
                    id="report-crop"
                    name="primaryCrop"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-700 focus:border-transparent text-sm bg-white cursor-pointer"
                  >
                    <option value="">Select a crop</option>
                    <option value="corn">Corn</option>
                    <option value="soybeans">Soybeans</option>
                    <option value="wheat">Wheat</option>
                    <option value="alfalfa">Alfalfa / Hay</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                {status === 'error' && (
                  <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg text-sm">
                    Something went wrong. Please try again or call us at 717-913-1780.
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="w-full bg-emerald-800 hover:bg-emerald-700 text-white font-bold px-6 py-3.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed text-sm flex items-center justify-center gap-2"
                >
                  <i className="ri-mail-send-line"></i>
                  {status === 'submitting' ? 'Sending...' : 'Send Me the Report'}
                </button>

                <p className="text-xs text-gray-400 text-center">
                  No spam. No obligation. Just useful data for your operation.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
